import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const noop = () => {};

const defaultCtx = {
  buildPhase: "outbound",
  setBuildPhase: noop,
  flashMessage: null,
  setFlashMessage: noop,
};

const SelfComposeUiContext = createContext(defaultCtx);

export function useSelfComposeUi() {
  return useContext(SelfComposeUiContext);
}

export function SelfComposeUiProvider({ enabled, children }) {
  const [buildPhase, setBuildPhaseState] = useState("outbound");
  const [flashMessage, setFlashMessageState] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setBuildPhaseState("outbound");
      setFlashMessageState(null);
    }
  }, [enabled]);

  const setBuildPhase = useCallback(
    (next) => {
      if (!enabled) return;
      setBuildPhaseState((prev) =>
        typeof next === "function" ? next(prev) : next
      );
    },
    [enabled]
  );

  const setFlashMessage = useCallback(
    (msg) => {
      if (!enabled) return;
      setFlashMessageState(msg || null);
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled || !flashMessage) return undefined;
    const id = window.setTimeout(() => setFlashMessageState(null), 4200);
    return () => window.clearTimeout(id);
  }, [enabled, flashMessage]);

  const value = useMemo(() => {
    if (!enabled) {
      return defaultCtx;
    }
    return {
      buildPhase,
      setBuildPhase,
      flashMessage,
      setFlashMessage,
    };
  }, [enabled, buildPhase, setBuildPhase, flashMessage, setFlashMessage]);

  return (
    <SelfComposeUiContext.Provider value={value}>
      {children}
    </SelfComposeUiContext.Provider>
  );
}
